import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, HandHeart, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUPPORT_OPTIONS = [
  { v: "virtual", l: "Virtual Support", d: "Nurse on your telehealth visit" },
  { v: "in_person", l: "In-Person (Orlando)", d: "Nurse beside you at the clinic" },
  { v: "interpreter", l: "Interpreter Support", d: "English ⇄ Haitian Creole" },
  { v: "nurse_advocate", l: "Nurse Advocate", d: "Pre + during + post appointment" },
];

export default function AttendWithMeModal({ open, onClose }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    appointment_date: "",
    clinic_name: "",
    appointment_type: "",
    support_type: "virtual",
    language: "english",
    notes: "",
  });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErr("");
    try {
      await axios.post(`${API}/attend-requests`, form);
      setStatus("success");
    } catch (e2) {
      setStatus("error");
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setStatus("idle");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        appointment_date: "",
        clinic_name: "",
        appointment_type: "",
        support_type: "virtual",
        language: "english",
        notes: "",
      });
    }, 300);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="attend-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-[#3F5043]/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#F8F7F3] rounded-3xl w-full max-w-xl max-h-[92vh] overflow-y-auto shadow-2xl"
          >
            <div className="sticky top-0 bg-[#F8F7F3] border-b border-[#BCB3A6]/40 px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#3F5043] text-[#AE8F78] flex items-center justify-center">
                  <HandHeart size={18} />
                </div>
                <div>
                  <div className="font-serif italic text-2xl text-[#3F5043] leading-tight">
                    Attend With Me
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#899480] font-semibold">
                    Request Soarlife Support
                  </div>
                </div>
              </div>
              <button
                onClick={close}
                data-testid="attend-close"
                className="w-9 h-9 rounded-full bg-[#EDEDEE] text-[#3F5043] flex items-center justify-center hover:bg-[#BCB3A6]/40"
              >
                <X size={16} />
              </button>
            </div>

            {status === "success" ? (
              <div
                data-testid="attend-success"
                className="p-10 text-center flex flex-col items-center"
              >
                <CheckCircle2 size={48} strokeWidth={1.2} className="text-[#899480] mb-5" />
                <h3 className="font-serif text-3xl text-[#3F5043] mb-3">
                  You won't walk in alone.
                </h3>
                <p className="text-[#3F5043]/75 max-w-md">
                  A Soarlife nurse will reach out within one business day to confirm your request and coordinate support.
                </p>
                <button
                  onClick={close}
                  className="mt-7 text-sm uppercase tracking-[0.22em] text-[#899480] hover:text-[#3F5043]"
                >
                  Close
                </button>
              </div>
            ) : (
              <div data-testid="attend-booking-redirect" className="p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-[#3F5043] text-[#AE8F78] flex items-center justify-center mx-auto mb-5">
                  <HandHeart size={24} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#3F5043] mb-3">
                  In-person support is now booked online
                </h3>
                <p className="text-sm text-[#3F5043]/75 leading-relaxed max-w-md mx-auto mb-6">
                  Reserve your Attend With Me companion or interpreter in a few taps — pick your date and
                  time, and your request is confirmed the moment it's accepted and paid.
                </p>
                <a
                  href="/book?service=interpreter"
                  data-testid="attend-book-link"
                  className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
                >
                  Book in-person support — from $80
                </a>
                <p className="text-xs text-[#3F5043]/60 mt-5 max-w-md mx-auto leading-relaxed">
                  Soarlife client with an IVF package? Request from your Client Portal with your support
                  code — just the $20/day admin fee.
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, required, testid, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
        {label}
        {required && <span className="text-[#875F45]"> *</span>}
      </label>
      <input
        data-testid={testid}
        required={required}
        className="w-full bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480]"
        {...rest}
      />
    </div>
  );
}
